import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';
import { tenantUrl } from '../utils/tenantHost';

const authHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const fetchTenants = async (token) => {
  const res = await fetch(`${BASE_URL}/tenants`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.content ?? []);
};

const ACCENT_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#7c3aed', '#dc2626'];
const colorFor = (str = '') => ACCENT_COLORS[str.charCodeAt(0) % ACCENT_COLORS.length];

const SkeletonCard = () => (
  <div className="card" style={{ padding: 20 }}>
    {[['60%', 16], ['45%', 12], ['100%', 28]].map(([w, h], i) => (
      <div key={i} className="skeleton" style={{ width: w, height: h, borderRadius: 6, marginBottom: 10 }} />
    ))}
  </div>
);

const TenantCard = ({ tenant, idx, onSelect, onEdit }) => {
  const color = colorFor(tenant.name);
  return (
    <div
      className="card"
      style={{
        cursor: 'pointer', padding: 20, transition: 'box-shadow .15s, transform .15s',
        animation: `fadeUp .35s ${idx * 40}ms ease both`, position: 'relative',
      }}
      onClick={() => onSelect(tenant)}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 8px 28px ${color}18`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(tenant); }}
        title="Edit tenant"
        style={{
          position: 'absolute', top: 14, right: 14, padding: '4px 10px', fontSize: 11.5, fontWeight: 600,
          borderRadius: 9999, border: '1px solid #e5e7eb', background: '#fff', color: '#4f46e5', cursor: 'pointer',
        }}
      >
        Edit
      </button>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: `linear-gradient(135deg, ${color}dd, ${color}88)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 700, color: '#fff', fontFamily: 'Sora, sans-serif',
        }}>
          {tenant.name.slice(0, 2).toUpperCase()}
        </div>
        <div style={{ minWidth: 0, paddingRight: 46 }}>
          <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 14.5, color: '#1e1b4b', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {tenant.name}
          </div>
          <div className="t-meta">{tenant.slug}</div>
          <div className="t-meta" style={{ color: tenant.subdomain ? '#4f46e5' : '#dc2626' }}>
            {tenant.subdomain
              ? new URL(tenantUrl(tenant.subdomain)).host
              : 'No subdomain — not reachable'}
          </div>
        </div>
      </div>

      {tenant.address && (
        <div className="t-body-sm" style={{ marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          📍 {tenant.address}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 9999,
          background: tenant.active !== false ? '#ecfdf5' : '#fef2f2',
          color: tenant.active !== false ? '#059669' : '#dc2626',
          border: `1px solid ${tenant.active !== false ? '#6ee7b7' : '#fca5a5'}`,
          fontFamily: 'DM Sans, sans-serif',
        }}>
          {tenant.active !== false ? 'Active' : 'Inactive'}
        </span>
        {tenant.phone && <span className="t-meta">📞 {tenant.phone}</span>}
      </div>
    </div>
  );
};

const EMPTY_FORM = { name: '', slug: '', subdomain: '', address: '', phone: '', active: true };

const Tenants = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null); // null = create mode, else tenant being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTenants(token);
      setTenants(data);
    } catch (e) {
      setError(`Could not load tenants (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = tenants.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (t.name || '').toLowerCase().includes(q)
      || (t.slug || '').toLowerCase().includes(q)
      || (t.subdomain || '').toLowerCase().includes(q);
  });

  const openEdit = (tenant) => {
    setEditingTenant(tenant);
    setForm({
      name: tenant.name || '',
      slug: tenant.slug || '',
      subdomain: tenant.subdomain || '',
      address: tenant.address || '',
      phone: tenant.phone || '',
      active: tenant.active !== false,
    });
    setFormError('');
    setShowCreateModal(true);
  };

  // Each tenant is its own site (and login session): open it on its subdomain.
  // A tenant without a subdomain can't be reached, so prompt to set one.
  const handleSelect = (tenant) => {
    if (tenant.subdomain) window.location.assign(tenantUrl(tenant.subdomain));
    else openEdit(tenant);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingTenant(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const handleSubmitTenant = async (e) => {
    e.preventDefault();
    if (!token) return;

    const name = form.name.trim();
    const slug = (form.slug || name).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const subdomain = form.subdomain.trim().toLowerCase();
    if (!name || !slug) {
      setFormError('Please provide a tenant name and slug.');
      return;
    }
    if (!/^[a-z0-9-]{2,63}$/.test(subdomain)) {
      setFormError('Subdomain is required: 2–63 lowercase letters, digits or hyphens.');
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      const isEdit = !!editingTenant;
      const res = await fetch(`${BASE_URL}/tenants${isEdit ? `/${editingTenant.id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          name,
          slug,
          subdomain,
          address: form.address.trim(),
          phone: form.phone.trim(),
          active: form.active,
        }),
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Unable to ${isEdit ? 'update' : 'create'} tenant`);
      }

      closeModal();
      await load();
    } catch (e) {
      setFormError(e.message || `Unable to ${editingTenant ? 'update' : 'create'} tenant`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <main className="page-main">

        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <span className="bc-current">Tenants</span>
        </div>

        <div className="fade-up" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.5rem' }}>
          <div>
            <h1 className="t-h1" style={{ marginBottom: 4 }}>🏥 Tenants</h1>
            <p className="t-body-sm">
              {!loading && !error && `${tenants.length} tenant${tenants.length !== 1 ? 's' : ''} registered`}
            </p>
          </div>
          <button
            className="t-link"
            onClick={() => { setEditingTenant(null); setShowCreateModal(true); }}
            style={{ padding: '8px 12px', border: '1px solid #4f46e5', borderRadius: 9999, background: '#eef2ff', color: '#4338ca' }}
          >
            + Add Tenant
          </button>
        </div>

        {/* Search */}
        <div className="card fade-up-1" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
          <div className="search-wrap" style={{ marginBottom: 0 }}>
            <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
              placeholder="Search by name, slug or subdomain…"
              style={{ height: 38, fontSize: 13 }}
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={load}>Retry</button>
          </div>
        )}

        <div className="fade-up-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : filtered.length === 0
              ? (
                <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                  <div className="empty-icon">🏥</div>
                  <p className="empty-title">{search ? 'No tenants match your search' : 'No tenants yet'}</p>
                </div>
              )
              : filtered.map((t, i) => <TenantCard key={t.id} tenant={t} idx={i} onSelect={handleSelect} onEdit={openEdit} />)
          }
        </div>

        {showCreateModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <div className="card" style={{ width: '100%', maxWidth: 480, padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20 }}>{editingTenant ? 'Edit Tenant' : 'Add New Tenant'}</h2>
                <button className="t-link" onClick={closeModal} style={{ fontSize: 18 }}>×</button>
              </div>
              {editingTenant && (
                <div style={{ fontSize: 12, color: '#a32d2d', marginBottom: 14 }}>
                  Changing the slug renames this tenant's underlying database schema — do this during a quiet period.
                </div>
              )}
              <form onSubmit={handleSubmitTenant}>
                <div style={{ display: 'grid', gap: 12 }}>
                  <label>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Name</div>
                    <input
                      required
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                    />
                  </label>
                  <label>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Slug</div>
                    <input
                      value={form.slug}
                      onChange={e => setForm({ ...form, slug: e.target.value })}
                      placeholder="Optional; auto-generated from name"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                    />
                  </label>
                  <label>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Subdomain</div>
                    <input
                      required
                      value={form.subdomain}
                      onChange={e => setForm({ ...form, subdomain: e.target.value })}
                      placeholder="e.g. manoj → manoj.trimedcare.in"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                    />
                  </label>
                  <label>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Address</div>
                    <input
                      value={form.address}
                      onChange={e => setForm({ ...form, address: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                    />
                  </label>
                  <label>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>Phone</div>
                    <input
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }}
                    />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={e => setForm({ ...form, active: e.target.checked })}
                    />
                    <span>Active tenant</span>
                  </label>
                  {formError && <div style={{ color: '#dc2626', fontSize: 13 }}>{formError}</div>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
                  <button type="button" className="t-link" onClick={closeModal} disabled={submitting}>Cancel</button>
                  <button type="submit" disabled={submitting} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', cursor: 'pointer' }}>
                    {submitting ? (editingTenant ? 'Saving…' : 'Creating…') : (editingTenant ? 'Save changes' : 'Create Tenant')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default Tenants;